import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type KeyboardEvent, type ReactNode, useState } from 'react';
import { Pill } from '../../Pill.js';
import { Chips, EYEBROW_SX, TILE_SX } from '../primitives.js';
import type { Scene, StoryboardElements } from './storyboardResult.js';

const SCENE_COLORS = ['#831DFB', '#155DFC', '#2C9C6D', '#A88924', '#CD5564'];
const EDITABLE_FIELDS = [
  ['visual', 'Visual'],
  ['on_screen_text', 'On-screen text'],
  ['voiceover', 'Voiceover'],
  ['audio', 'Audio'],
  ['cta', 'Call to action'],
] as const;

export type EditableSceneField = (typeof EDITABLE_FIELDS)[number][0];

function EditableCopy({
  label,
  value,
  editable,
  onCommit,
}: {
  label: string;
  value: string;
  editable: boolean;
  onCommit: (value: string) => void;
}): ReactNode {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const commit = (): void => {
    onCommit(draft);
    setEditing(false);
  };
  const cancel = (): void => {
    setDraft(value);
    setEditing(false);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      cancel();
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      commit();
    }
  };

  if (editing) {
    return (
      <TextField
        autoFocus
        multiline
        size="small"
        label={label}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        helperText="Enter to apply · Shift+Enter for a new line · Esc to cancel"
        fullWidth
      />
    );
  }
  if (!editable && value === '') return null;
  return (
    <Stack
      component={editable ? 'button' : 'div'}
      type={editable ? 'button' : undefined}
      aria-label={editable ? `Edit ${label}` : undefined}
      onClick={editable ? () => setEditing(true) : undefined}
      sx={{
        gap: 0.5,
        minWidth: 0,
        p: 0,
        border: 0,
        bgcolor: 'transparent',
        color: 'inherit',
        textAlign: 'left',
        cursor: editable ? 'text' : 'default',
      }}
    >
      <Typography sx={EYEBROW_SX}>{label}</Typography>
      <Typography
        component="div"
        sx={{ fontSize: 12, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', textWrap: 'pretty' }}
      >
        {value || 'Click to add'}
      </Typography>
    </Stack>
  );
}

export function sceneTint(index: number): string {
  // Both callers supply non-negative Array.map indices.
  return alpha(SCENE_COLORS[index % SCENE_COLORS.length] as string, 0.1);
}

export function timecode(scene: Scene): string {
  return scene.start_seconds === null || scene.end_seconds === null
    ? 'Static frame'
    : `${scene.start_seconds}–${scene.end_seconds}s`;
}

export function SceneCard({
  scene,
  index,
  elements,
  editable = false,
  onChange,
}: {
  scene: Scene;
  index: number;
  elements: StoryboardElements;
  editable?: boolean;
  onChange?: (field: EditableSceneField, value: string) => void;
}): ReactNode {
  return (
    <Stack
      data-testid={`scene-card-${index}`}
      data-editable={editable}
      sx={{
        ...TILE_SX,
        p: '12px',
        pb: '16px',
        gap: '20px',
        borderRadius: '18px',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        ...(editable
          ? {
              '&:hover, &:focus-within': {
                borderColor: 'primary.main',
                boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
              },
            }
          : {}),
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: '8px',
          alignItems: 'center',
          flexWrap: 'wrap',
          pb: '12px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Pill
          sx={{
            bgcolor: sceneTint(index),
            color: 'text.secondary',
            borderRadius: '6px',
            fontSize: 10,
            px: '6px',
            py: '4px',
          }}
        >
          {timecode(scene)}
        </Pill>
        <Typography sx={{ fontSize: 14, fontWeight: 600, flex: 1 }}>
          {scene.objective || scene.shot_label}
        </Typography>
        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
          {[scene.framing, scene.camera_style].filter(Boolean).join(' · ')}
        </Typography>
      </Stack>
      {EDITABLE_FIELDS.map(([field, label]) => (
        <EditableCopy
          key={field}
          label={label}
          value={scene[field]}
          editable={editable}
          onCommit={(value) => onChange?.(field, value)}
        />
      ))}
      {scene.element_ids.length === 0 ? null : (
        <Chips values={scene.element_ids.map((id) => elements[id]?.tag ?? id)} />
      )}
    </Stack>
  );
}
